import _ from 'lodash';

import { mapGetters } from 'vuex';

import { config } from '_/config/config';

/**
 * Base test data
 *
 * @type  {Object}
 */
const baseTestData = {
    canCreateSearchChoice: false,
    disabled: false,
    flattenToOptgroups: false,
    id: 'testId',
    isActiveEntryFilter: null,
    label: 'Test Label',
    lazyLoading: false,
    multiple: false,
    mutation: 'testing/DEFAULT_TEST_MUTATION',
    name: 'testName',
    optionsData: [
        {
            id: 1,
            text: 'A Option',
        },
        {
            id: 2,
            text: 'B Option',
        },
        {
            id: 3,
            text: 'C Option',
        },
    ],
    placeholder: 'Test placeholder',
    preselectFirst: false,
    relatedTableName: false,
    required: false,
    tooltip: 'Testing the Tooltip.',
    value: null,
    vueComponent: 'Relationship',
};

export default {
    data() {
        return {
            /**
             * An object sent to child components; used to signal that the fields need to be reset, with the ability
             * to limit the reset to only certain fields
             *
             * @type  {Object}
             */
            formFieldsReset: {
                /**
                 * A list of field names to include in a reset action
                 *
                 * @type  {Array}
                 */
                includeFields: [],

                /**
                 * A parameter that is listened by the child components. Changed via moment().valueOf() method to always
                 * set the fresh value.
                 *
                 * @type  {integer}
                 */
                resetData: null,
            },

            /**
             * Tests
             *
             * @type  {Object}
             */
            tests: {
                testDefaultSettings: {
                    ...baseTestData,
                },

                testSingleValueSelection: {
                    ...baseTestData,
                    value: 3,
                },

                testDisabled: {
                    ...baseTestData,
                    disabled: true,
                },

                testRequired: {
                    ...baseTestData,
                    required: true,
                },

                testPreselectFirst: {
                    ...baseTestData,
                    preselectFirst: true,
                },

                testMultiple: {
                    ...baseTestData,
                    multiple: true,
                },

                testMultipleValueSelection: {
                    ...baseTestData,
                    multiple: true,
                    value: [1, 3],
                },

                testCanUseStringAsOptionId: {
                    ...baseTestData,
                    optionsData: [
                        {
                            id: 'testID1',
                            text: 'A Option',
                        },
                        {
                            id: 'testID2',
                            text: 'B Option',
                        },
                    ],
                },

                testRemoteDataSource: {
                    ...baseTestData,
                    optionsData: null,
                    relatedTableName: 'users',
                    value: 1,
                },

                testRemoteDataSourceMultiple: {
                    ...baseTestData,
                    multiple: true,
                    optionsData: null,
                    relatedTableName: 'users',
                    value: [2, 1],
                },

                testRemoteDataSourceLazySelection: {
                    ...baseTestData,
                    optionsData: null,
                    lazyLoading: true,
                    relatedTableName: 'users',
                },

                testCanCreateSearchChoice: {
                    ...baseTestData,
                    canCreateSearchChoice: true,
                    lazyLoading: true,
                    multiple: true,
                    optionsData: null,
                    relatedTableName: 'file_tags',
                },

                testPreloadDataConfig: {
                    ...baseTestData,
                    optionsData: null,
                    preloadDataConfig: {
                        method: 'post',
                        payload: {
                            filter: {
                                id: {
                                    equal: '1',
                                },
                            },
                        },
                        modules: [users],
                        resultsObjectPath: 'body.body.entries',
                        valuesOfInterest: {
                            id: 'system.id',
                            text: 'system.anchor_text',
                        },
                        url: `${config.ENV.apiBasePath}/filter`,
                    },
                    value: 1,
                },

                testIsActiveEntryFilter: {
                    ...baseTestData,
                    isActiveEntryFilter: 'locked',
                    optionsData: null,
                    relatedTableName: 'users',
                },

                testFlattenToOptgroups: {
                    ...baseTestData,
                    flattenToOptgroups: true,
                    optionsData: null,
                    relatedTableName: 'menu_items',
                },
            },

            /**
             * Selected test data
             *
             * @type  {Object}
             */
            test: {},
        };
    },

    /**
     * Set the computed property
     *
     * @type  {Object}
     */
    computed: {
        ...mapGetters({
            testingState: 'testing/testing',
        }),

        /**
         * Toggles the tested component
         *
         * @return  {boolean}
         */
        showComponent() {
            return !_.isEmpty(this.test);
        },
    },

    /**
     * Set the mounted hook
     *
     * @return  {void}
     */
    mounted() {
        this.test = this.tests[this.$route.params.test];
    },
};
